// Global
import { Field, Item, Text as SitecoreText } from '@sitecore-jss/sitecore-jss-nextjs';
import { TextProps } from '@sitecore-jss/sitecore-jss-react/types/components/Text';
import React from 'react';
import { classnames, TArg, TTailwindString } from 'tailwindcss-classnames';
// Lib
import HeadingTag from 'components/helpers/HeadingTag';
import HeadingCallout from 'components/helpers/Heading/HeadingCallout';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import { getHeadingAlign, setHeadingSize, ValidHeadingLevels } from 'lib/sitecore/component-params';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { getEnumValue } from 'lib/util/enum';

export type HeadingSizes = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
export type HeadingAlign = 'left' | 'right' | 'center';

export type HeadingProps = {
  className?: TTailwindString | TArg;
  calloutText?: Field<string>;
  description?: Field<string>;
  descriptionClassName?: TTailwindString;
  descriptionDisabled?: boolean;
  headingLevel?: ValidHeadingLevels;
  headingSize?: HeadingSizes;
  headingAlignment?: HeadingAlign;
  headingText?: Field<string>;
  id?: string;
  editable?: boolean;
};

export type BaseHeadingFields = {
  headingLevel?: Item;
  headingSize?: Item;
  headingAlignment?: Item;
  headingText: Field<string>;
  headingTextOverride?: Field<string>;
  calloutText?: Field<string>;
  calloutTextOverride?: Field<string>;
  description?: Field<string>;
  descriptionOverride?: Field<string>;
  [name: string]: unknown;
};

type BaseGraphqlHeadingFields = {
  datasource?: {
    calloutText?: { jsonValue: Field<string> };
    description?: { jsonValue: Field<string> };
    headingLevel?: { jsonValue: Item };
    headingSize?: { jsonValue: Item };
    headingAlignment?: { jsonValue: Item };
    headingText: { jsonValue: Field<string> };
    [name: string]: unknown;
  };
};

const defaultProps: HeadingProps = {
  headingLevel: 'h2',
  headingSize: 'l',
  headingAlignment: 'left',
  headingText: {
    value: '',
  },
  description: {
    value: '',
  },
  descriptionClassName: classnames(),
  descriptionDisabled: false,
};

export const propsFromFields = (fields: BaseHeadingFields, editable = true): HeadingProps => {
  const data = { ...defaultProps, editable };

  // default to h2 if no value is set
  if (fields.headingLevel && data.headingLevel) {
    data.headingLevel = getEnumValue<ValidHeadingLevels>(fields.headingLevel, 'h2');
  }
  if (fields.headingAlignment) {
    data.headingAlignment = getEnumValue<HeadingAlign>(fields.headingAlignment, 'left');
  }
  if (fields.headingSize) {
    data.headingSize = getEnumValue<HeadingSizes>(fields.headingSize);
  }

  // We'll still hide the heading if text is falsey in production so checking for non-null
  // editable is still safe here
  if (fields.headingTextOverride?.value?.length) {
    data.headingText = fields.headingTextOverride;
  } else if (!!fields.headingText?.value || !!fields.headingText?.editable) {
    data.headingText = fields.headingText;
  }

  if (fields.descriptionOverride?.value?.length) {
    data.description = fields.descriptionOverride;
  } else if (!!fields.description?.value || !!fields.description?.editable) {
    data.description = fields.description;
  }

  if (fields.calloutTextOverride?.value?.length) {
    data.calloutText = fields.calloutTextOverride;
  } else if (!!fields.calloutText?.value || !!fields.calloutText?.editable) {
    data.calloutText = fields.calloutText;
  }

  return data;
};

export const propsFromGraphqlData = ({ datasource }: BaseGraphqlHeadingFields): HeadingProps => {
  const data = { ...defaultProps };
  if (datasource === null || datasource === undefined) {
    return data;
  }

  // default to h2 if no value is set
  if (datasource.headingLevel?.jsonValue) {
    data.headingLevel = getEnumValue<ValidHeadingLevels>(datasource.headingLevel.jsonValue) || 'h2';
  }

  if (datasource.headingSize?.jsonValue) {
    data.headingSize = getEnumValue<HeadingSizes>(datasource.headingSize.jsonValue);
  }

  // We'll still hide the heading if text is falsey in production so checking for non-null
  // editable is still safe here
  if (!!datasource.headingText?.jsonValue.value || !!datasource.headingText?.jsonValue.editable) {
    data.headingText = datasource.headingText.jsonValue;
  }

  if (!!datasource.description?.jsonValue?.value || !!datasource.description?.jsonValue.editable) {
    data.description = datasource.description.jsonValue;
  }

  if (!!datasource.calloutText?.jsonValue?.value || !!datasource.calloutText?.jsonValue?.editable) {
    data.calloutText = datasource.calloutText.jsonValue;
  }

  return data;
};

export const TextWithOptionalLineBreaks = (props: TextProps) => {
  const { field } = props;
  const isExperienceEditor = useExperienceEditor();

  if (!field) {
    return <></>;
  }

  return isExperienceEditor ? (
    <SitecoreText {...props} />
  ) : (
    <>
      {React.Children.toArray(
        (field.value as string)?.split(/\r?\n/).map((x) => {
          return (
            <>
              <span className={classnames('inline-block')}>{x}</span>{' '}
            </>
          );
        })
      )}
    </>
  );
};

const Heading = ({
  calloutText,
  className,
  description,
  descriptionClassName,
  descriptionDisabled = false,
  headingLevel,
  headingSize,
  headingText,
  headingAlignment,
  id,
  editable = true,
}: HeadingProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();
  const hasDescription = description && description?.value?.length > 0;
  const headingAlign = getHeadingAlign(headingAlignment);

  const headingClasses = classnames(
    setHeadingSize(headingSize),
    headingAlign,
    {
      'max-w-prose': headingAlignment === 'left',
      'mb-4': hasDescription && (!className || descriptionDisabled),
      'mb-8': !hasDescription && !className && headingSize !== 'xl',
      'mb-12': !hasDescription && !className && headingSize === 'xl',
    },
    ...(className ? [className] : [])
  );

  const descriptionClass = classnames(descriptionClassName, headingAlign);

  const renderHeading = headingText?.value?.trim()?.length || isExperienceEditor;

  const headingElement = renderHeading ? (
    <HeadingTag
      headingLevel={headingLevel as ValidHeadingLevels}
      className={headingClasses}
      component-name="helper/heading"
      data-testid="heading-heading-element"
      id={id}
    >
      <TextWithOptionalLineBreaks tag="" field={headingText} editable={editable} />
    </HeadingTag>
  ) : (
    <></>
  );

  return (
    <>
      <HeadingCallout
        calloutText={calloutText}
        headingAlignClassName={headingAlign}
        headingLevel={headingLevel}
        editable={editable}
      />
      {headingElement}
      {!descriptionDisabled && (
        <RichTextA11yWrapper
          className={classnames('p-0', 'rte', descriptionClass)}
          field={description}
          editable={editable}
        />
      )}
    </>
  );
};

export default Heading;
